import { defineRule } from 'vee-validate';
import ibanLen from './helpers/IBANLen.js';
import { isValid, isEqual, isAfter, isBefore } from 'date-fns';

defineRule('required', (value, [translationKey]) => {
  if (!value || !value.length) {
    return translationKey ? `validators.${translationKey}` : 'validators.required-field';
  }
  return true;
});

defineRule('requiredNumber', (value, [translationKey]) => {
  if (value === '' || value === false || value === null || value === undefined) {
    return translationKey ? `validators.${translationKey}` : 'validators.required-field';
  }
  return true;
});

defineRule('requiredDropdown', (value, [translationKey]) => {
  if (!value || Object.keys(value).length < 2) {
    return translationKey ? `validators.${translationKey}` : 'validators.required-field';
  }
  return true;
});
defineRule('requiredDropdownMultiChoice', (value, [translationKey]) => {
  if (
    !value ||
    Object.keys(value).length < 1 ||
    (Object.keys(value).length === 1 && Object.values(value)[0].key === 0)
  ) {
    return translationKey ? `validators.${translationKey}` : 'validators.required-field';
  }
  return true;
});
defineRule('checked', (value, [translationKey]) => {
  const key =
    translationKey !== null && translationKey !== undefined
      ? `validators.${translationKey}`
      : 'validators.no-confirmation';
  return value ? true : key;
});

defineRule('isValidIban', (iban) => {
  const validationMessage = 'validators.valid-iban';
  if (!iban) return true;

  iban = iban.replace(/\s/g, '');

  if (!iban.match(/^[\dA-Z]+$/)) return validationMessage;
  var len = iban.length;
  if (len !== ibanLen[iban.substring(0, 2)]) return validationMessage;
  iban = iban.substring(4) + iban.substring(0, 4);
  for (var s = '', i = 0; i < len; i += 1) s += parseInt(iban.charAt(i), 36);
  // eslint-disable-next-line
  for (var m = s.substring(0, 15) % 97, s = s.substring(15); s; s = s.substring(13)) m = (m + s.substring(0, 13)) % 97;

  return m === 1 ? true : validationMessage;
});

defineRule('isValidFileType', (value, { selectedFiles, allowedFileTypes }) => {
  let error = false;
  for (let i = 0; i < selectedFiles.length; i++) {
    const fileType = `.${selectedFiles[i].fileName.split('.').pop()}`.toLocaleLowerCase();
    if (!allowedFileTypes.split(',').includes(fileType)) {
      selectedFiles[i].isInvalid = true;
      error = true;
    }
  }

  return error ? 'validators.invalid-file-type' : true;
});

defineRule('isFileSelected', (value, { selectedFiles }) => {
  return selectedFiles.length === 0 ? 'validators.no-file-selected' : true;
});

defineRule('isFileToBig', (value, { usedMB, maxMB }) => {
  return usedMB > maxMB ? 'validators.file-to-big' : true;
});

defineRule('isValidValuta', (value, [translationKey]) => {
  const errorMessage = translationKey ? `validators.${translationKey}` : 'validators.invalid-input';
  if (typeof value === 'boolean' || value === 0) return errorMessage;

  if (value.toString().match(/^\d+(\.\d{1,2})?$/)) return true;
  return errorMessage;
});

defineRule('isDutchPostalCode', (value) => {
  value = value.replace(/\s/g, '');
  if (!value.match(/^\d{4}[A-Z]{2}$/)) return 'validators.invalid-postal-code';
  return true;
});

defineRule('isValidDate', (date, [min, max, messageKeyTooLongAgo, messageKeyFuture]) => {
  const minDate = min && min !== 'undefined' ? new Date(min) : undefined;
  const maxDate = max && max !== 'undefined' ? new Date(max) : undefined;

  if (!date) return true;
  var parsedDate = new Date(date);
  if (!isValid(parsedDate)) return 'validators.invalid-date';
  else if (minDate && (isEqual(parsedDate, minDate) || isBefore(parsedDate, minDate)))
    return `validators.${messageKeyTooLongAgo}`;
  else if (maxDate && (isEqual(parsedDate, maxDate) || isAfter(parsedDate, maxDate)))
    return `validators.${messageKeyFuture}`;
  return true;
});

defineRule('isValidDateCanBeEqual', (date, [min, max, messageKeyTooLongAgo, messageKeyFuture]) => {
  const minDate = min && min !== 'undefined' ? new Date(min) : undefined;
  const maxDate = max && max !== 'undefined' ? new Date(max) : undefined;

  if (!date) return true;
  var parsedDate = new Date(date);
  if (!isValid(parsedDate)) return 'validators.invalid-date';
  else if (minDate && isBefore(parsedDate, minDate)) return `validators.${messageKeyTooLongAgo}`;
  else if (maxDate && isAfter(parsedDate, maxDate)) return `validators.${messageKeyFuture}`;
  return true;
});

defineRule('isAmountBetween', (value, { min, max, minTranslationKey, maxTranslationKey }) => {
  if (value > max) return `validators.${maxTranslationKey}`;
  if (value < min) return `validators.${minTranslationKey}`;
  return true;
});

defineRule('isValidEmail', (email) => {
  if (!email) return 'validators.email-required';
  if (!email?.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/)) return 'validators.email-invalid';
  return true;
});

defineRule('isValidPhoneNumber', (value, { prefix, number }) => {
  if (!number) return 'validators.phone-number-required';
  let phoneNumberParsed = null;
  let val = '';

  if (prefix?.value && number) {
    val = prefix.value.replace('(', '').replace(')', '');
    phoneNumberParsed = `${val}${number}`;
  }

  if (
    !phoneNumberParsed ||
    !phoneNumberParsed?.startsWith('+') ||
    (phoneNumberParsed?.startsWith('+31') && phoneNumberParsed?.length !== 12) ||
    phoneNumberParsed?.length <= 9 ||
    phoneNumberParsed?.length > 15
  ) {
    return 'validators.phone-number-invalid';
  }

  return true;
});

defineRule('numberLength', (value, [number, requiredLength, translationKey]) => {
  if (number.length < requiredLength) return `validators.${translationKey}`;
  return true;
});

defineRule('numberIsPositive', (value, [number, translationKey]) => {
  if (number < 1) return `validators.${translationKey}`;
  return true;
});

defineRule('validSocialSecurityNumber', (value) => {
  if (!value || value.length <= 8 || value.length > 9) {
    return `validators.social-security-number`;
  }
  if (!value.match(/^\d{8,9}$/)) {
    return `validators.social-security-number`;
  }
  var j = value.length;
  var pos = 0;
  var result = 0;
  for (let i = j; i > 0; i--) {
    result += i !== 1 ? value.charAt(pos) * i : value.charAt(pos) * i * -1;
    pos++;
  }
  if (result === 0 || result % 11 !== 0) {
    return `validators.social-security-number`;
  }
  return result % 11 === 0;
});
